import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig'; // Adjust the import based on your project structure
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Grid, Card, CardContent, Box } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import DirectionsIcon from '@mui/icons-material/Directions';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Fix marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const HospitalTable = () => {
    const [hospitals, setHospitals] = useState([]);
    const [filteredHospitals, setFilteredHospitals] = useState([]);
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const [selectedSpecialty, setSelectedSpecialty] = useState('All');
    const [specialtyGroups, setSpecialtyGroups] = useState({});

    // Function to create specialty groups
    const createSpecialtyGroups = (specialties, hospitals) => {
        const groups = {
            'All': { label: 'All', specialties: [], hospitalCount: hospitals.length },
            'Peds': { label: 'Pediatrics', specialties: [], hospitalCount: 0 },
            'Trauma': { label: 'Trauma', specialties: [], hospitalCount: 0 },
            'Cardiac': { label: 'Cardiac', specialties: [], hospitalCount: 0 },
            'Neuro': { label: 'Neurology', specialties: [], hospitalCount: 0 },
            'Stroke': { label: 'Stroke', specialties: [], hospitalCount: 0 },
            'Ortho': { label: 'Orthopedics', specialties: [], hospitalCount: 0 },
            'Other': { label: 'Other', specialties: [], hospitalCount: 0 }
        };

        // First, collect all specialties for each group
        specialties.forEach(specialty => {
            const lowerSpecialty = specialty.toLowerCase();
            
            if (lowerSpecialty.includes('peds') || lowerSpecialty.includes('pediatric')) {
                groups['Peds'].specialties.push(specialty);
            } else if (lowerSpecialty.includes('trauma')) {
                groups['Trauma'].specialties.push(specialty);
            } else if (lowerSpecialty.includes('cardiac') || lowerSpecialty.includes('heart')) {
                groups['Cardiac'].specialties.push(specialty);
            } else if (lowerSpecialty.includes('stroke') || lowerSpecialty.includes('cva')) {
                groups['Stroke'].specialties.push(specialty);
            } else if (lowerSpecialty.includes('neuro') || lowerSpecialty.includes('brain')) {
                groups['Neuro'].specialties.push(specialty);
            } else if (lowerSpecialty.includes('ortho') || lowerSpecialty.includes('bone')) {
                groups['Ortho'].specialties.push(specialty);
            } else {
                groups['Other'].specialties.push(specialty);
            }
        });

        // Then count hospitals for each group
        Object.keys(groups).forEach(groupKey => {
            if (groupKey !== 'All') {
                const group = groups[groupKey];
                group.hospitalCount = hospitals.filter(hospital => {
                    const hospitalSpecialties = hospital.specialty?.toLowerCase().split(',').map(s => s.trim()) || [];
                    return group.specialties.some(groupSpecialty => 
                        hospitalSpecialties.includes(groupSpecialty.toLowerCase())
                    );
                }).length;
            }
        });

        // Remove empty groups
        Object.keys(groups).forEach(key => {
            if (groups[key].hospitalCount === 0 && key !== 'All') {
                delete groups[key];
            }
        });

        return groups;
    };

    useEffect(() => {
        const fetchHospitals = async () => {
            try {
                const hospitalRef = collection(db, 'hvac', 'GenericData', 'HospitalData');
                const querySnapshot = await getDocs(hospitalRef);
                const data = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Sort hospitals alphabetically by name
                const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));

                setHospitals(sortedData);
                setFilteredHospitals(sortedData);

                // Extract unique autocomplete options from specialties
                const specialties = [...new Set(sortedData.flatMap(hospital => hospital.specialty?.split(',').map(item => item.trim().toLowerCase()) || []))];
                setAutocompleteOptions(specialties.sort());
                
                // Create specialty groups
                const groups = createSpecialtyGroups(specialties, sortedData);
                setSpecialtyGroups(groups);
            } catch (error) {
                console.error('Error fetching hospital data:', error);
            }
        };

        fetchHospitals();
    }, []);

    const handleHospitalClick = (hospital) => {
        setSelectedHospital(hospital);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedHospital(null);
    };

    const handleInputChange = (event, value) => {
        setSearchText(value || '');
        applyFilters(value || '', selectedSpecialty);
    };

    const handleSpecialtyChange = (specialty) => {
        setSelectedSpecialty(specialty);
        applyFilters(searchText, specialty);
    };

    const applyFilters = (searchValue, specialty) => {
        let filtered = hospitals;

        // Apply specialty filter
        if (specialty !== 'All') {
            const group = specialtyGroups[specialty];
            if (group && group.specialties.length > 0) {
                filtered = filtered.filter(hospital => {
                    const hospitalSpecialties = hospital.specialty?.toLowerCase().split(',').map(s => s.trim()) || [];
                    return group.specialties.some(groupSpecialty => 
                        hospitalSpecialties.includes(groupSpecialty.toLowerCase())
                    );
                });
            }
        }

        // Apply search filter
        if (searchValue) {
            filtered = filtered.filter(hospital =>
                hospital.specialty?.toLowerCase().includes(searchValue.toLowerCase()) ||
                hospital.name?.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        setFilteredHospitals(filtered);
    };

    const handleKeyDown = (event) => {
        // Close keyboard on Enter key press
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent default form submission
            document.activeElement.blur(); // Close the keyboard
        }
    };

    return (
        <div style={{ 
            padding: '20px',
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {/* Search Bar */}
            <Box style={{ 
                marginBottom: '32px',
                maxWidth: '100%',
                overflow: 'hidden'
            }}>
                <Autocomplete
                    freeSolo
                    options={autocompleteOptions}
                    value={searchText}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search by Specialty or Hospital"
                            fullWidth
                            style={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.1)'
                                }
                            }}
                        />
                    )}
                />
            </Box>

            {/* Specialty Filter Buttons */}
            <Box style={{ 
                marginBottom: '24px', 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '4px', 
                justifyContent: 'center',
                padding: '0 4px',
                maxWidth: '100%',
                overflow: 'hidden',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                {Object.entries(specialtyGroups).map(([key, group]) => (
                    <Button
                        key={key}
                        variant={selectedSpecialty === key ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => handleSpecialtyChange(key)}
                        style={{
                            borderRadius: '16px',
                            textTransform: 'none',
                            fontWeight: '600',
                            padding: '4px 8px',
                            minWidth: '60px',
                            fontSize: '0.8rem',
                            flex: '0 0 auto',
                            maxWidth: '120px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            height: '32px',
                            lineHeight: '1.2'
                        }}
                        title={group.specialties.length > 0 ? `Includes: ${group.specialties.join(', ')}` : ''}
                    >
                        <span style={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {group.label}
                            {group.hospitalCount > 0 && (
                                <span style={{ 
                                    marginLeft: '4px', 
                                    fontSize: '0.75rem', 
                                    opacity: 0.7 
                                }}>
                                    ({group.hospitalCount})
                                </span>
                            )}
                        </span>
                    </Button>
                ))}
            </Box>

            <Grid container spacing={3}>
                {filteredHospitals.map((hospital) => (
                    <Grid item xs={12} sm={6} md={4} key={hospital.id}>
                        <Card
                            onClick={() => handleHospitalClick(hospital)}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: '#ffffff',
                                border: '2px solid #1976d2',
                                borderRadius: '16px',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.1)',
                                transition: 'all 0.3s ease',
                                height: '100%',
                                minHeight: '420px',
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.15)',
                                    border: '2px solid #1565c0'
                                }
                            }}
                        >
                            <CardContent style={{ 
                                padding: '24px',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                height: '100%'
                            }}>
                                <Typography variant="h5" style={{ 
                                    fontWeight: '700',
                                    color: '#1976d2',
                                    fontSize: '1.4rem',
                                    lineHeight: '1.3',
                                    marginBottom: '8px'
                                }}>
                                    {hospital.name}
                                </Typography>
                                <Box style={{ 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                    flex: 1
                                }}>
                                    <Box style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '8px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '8px'
                                    }}>
                                        <Typography variant="body2" style={{ 
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            minWidth: '90px'
                                        }}>
                                            MIEMSS ID:
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#333' }}>
                                            {hospital.miemssID}
                                        </Typography>
                                    </Box>
                                    <Box style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '8px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '8px'
                                    }}>
                                        <Typography variant="body2" style={{ 
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            minWidth: '90px'
                                        }}>
                                            Address:
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#333' }}>
                                            {hospital.address}
                                        </Typography>
                                    </Box>
                                    <Box style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '8px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '8px'
                                    }}>
                                        <Typography variant="body2" style={{ 
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            minWidth: '90px'
                                        }}>
                                            Door Code:
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#333' }}>
                                            {hospital.doorCode}
                                        </Typography>
                                    </Box>
                                    <Box style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '8px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '8px'
                                    }}>
                                        <Typography variant="body2" style={{ 
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            minWidth: '90px'
                                        }}>
                                            EMS Code:
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#333' }}>
                                            {hospital.emsCode}
                                        </Typography>
                                    </Box>
                                    <Box style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        padding: '8px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '8px'
                                    }}>
                                        <Typography variant="body2" style={{ 
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            minWidth: '90px'
                                        }}>
                                            Specialty:
                                        </Typography>
                                        <Box style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            {hospital.specialty.split(',').map((spec, index) => (
                                                <span key={index} style={{ 
                                                    backgroundColor: '#e3f2fd',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    color: '#1976d2'
                                                }}>{spec.trim()}</span>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '8px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '8px'
                                    }}>
                                        <Typography variant="body2" style={{ 
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            minWidth: '90px'
                                        }}>
                                            Bikur Cholim:
                                        </Typography>
                                        {hospital.bikurCholim ? 
                                            <CheckCircleIcon color="success" style={{ fontSize: '24px' }} /> : 
                                            <CancelIcon color="error" style={{ fontSize: '24px' }} />
                                        }
                                    </Box>
                                </Box>
                                <Box display="flex" justifyContent="center" mt="auto" pt={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        startIcon={<DirectionsIcon />}
                                        style={{
                                            borderRadius: '12px',
                                            padding: '10px 24px',
                                            textTransform: 'none',
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                                            '&:hover': {
                                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)'
                                            }
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation(); // Prevents the click from triggering the card click
                                            if (hospital.latitude && hospital.longitude) {
                                                const isMobile = /Mobi|Android/i.test(navigator.userAgent); // Check if the user is on a mobile device
                                                let mapUrl;

                                                if (isMobile) {
                                                    // Use geo URL for mobile devices
                                                    mapUrl = `geo:${hospital.latitude},${hospital.longitude}?q=${hospital.latitude},${hospital.longitude}(${encodeURIComponent(hospital.name)})`;
                                                } else {
                                                    // Use Google Maps URL for desktop
                                                    mapUrl = `https://www.google.com/maps/dir/?api=1&destination=${hospital.latitude},${hospital.longitude}&destination_name=${encodeURIComponent(hospital.name)}`;
                                                }

                                                // Open the map URL
                                                window.location.href = mapUrl; // Use window.location.href for better compatibility
                                            } else {
                                                console.error('No address data available for this hospital.');
                                            }
                                        }}
                                    >
                                        Get Directions
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {selectedHospital && (
                <Dialog 
                    open={open} 
                    onClose={handleClose} 
                    maxWidth="lg" 
                    fullWidth
                    PaperProps={{
                        style: {
                            borderRadius: '20px',
                            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)',
                            border: '2px solid #1976d2'
                        }
                    }}
                >
                    <DialogTitle style={{
                        backgroundColor: '#f5f9ff',
                        borderBottom: '2px solid #1976d2',
                        padding: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="h5" style={{
                            fontWeight: '700',
                            color: '#1976d2',
                            fontSize: '1.5rem'
                        }}>
                            {selectedHospital.name}
                        </Typography>
                        <IconButton
                            edge="end"
                            color="primary"
                            onClick={handleClose}
                            aria-label="close"
                            style={{
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.1)',
                                '&:hover': {
                                    backgroundColor: '#f5f9ff'
                                }
                            }}
                        >
                            <CancelIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent style={{ padding: '32px' }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px'
                                }}>
                                    <Box style={{
                                        padding: '16px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '12px',
                                        border: '1px solid #e3f2fd'
                                    }}>
                                        <Typography variant="subtitle1" style={{
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            marginBottom: '8px'
                                        }}>
                                            Contact Information
                                        </Typography>
                                        <Box style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Typography variant="body2" style={{ fontWeight: '600', color: '#666', minWidth: '100px' }}>
                                                    Address:
                                                </Typography>
                                                <Typography variant="body2" style={{ color: '#333' }}>
                                                    {selectedHospital.address}
                                                </Typography>
                                            </Box>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Typography variant="body2" style={{ fontWeight: '600', color: '#666', minWidth: '100px' }}>
                                                    Phone:
                                                </Typography>
                                                <Typography variant="body2" style={{ color: '#333' }}>
                                                    <a href={`tel:${selectedHospital.phone}`} style={{
                                                        color: '#1976d2',
                                                        textDecoration: 'none',
                                                        fontWeight: '500',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}>
                                                        {selectedHospital.phone}
                                                    </a>
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box style={{
                                        padding: '16px',
                                        backgroundColor: '#f5f9ff',
                                        borderRadius: '12px',
                                        border: '1px solid #e3f2fd'
                                    }}>
                                        <Typography variant="subtitle1" style={{
                                            fontWeight: '600',
                                            color: '#1976d2',
                                            marginBottom: '8px'
                                        }}>
                                            Hospital Details
                                        </Typography>
                                        <Box style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Typography variant="body2" style={{ fontWeight: '600', color: '#666', minWidth: '100px' }}>
                                                    Bikur Cholim:
                                                </Typography>
                                                {selectedHospital.bikurCholim ? 
                                                    <CheckCircleIcon color="success" style={{ fontSize: '24px' }} /> : 
                                                    <CancelIcon color="error" style={{ fontSize: '24px' }} />
                                                }
                                            </Box>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Typography variant="body2" style={{ fontWeight: '600', color: '#666', minWidth: '100px' }}>
                                                    Door Code:
                                                </Typography>
                                                <Typography variant="body2" style={{ color: '#333' }}>
                                                    {selectedHospital.doorCode}
                                                </Typography>
                                            </Box>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Typography variant="body2" style={{ fontWeight: '600', color: '#666', minWidth: '100px' }}>
                                                    MIEMSS ID:
                                                </Typography>
                                                <Typography variant="body2" style={{ color: '#333' }}>
                                                    {selectedHospital.miemssID}
                                                </Typography>
                                            </Box>
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Typography variant="body2" style={{ fontWeight: '600', color: '#666', minWidth: '100px' }}>
                                                    Specialty:
                                                </Typography>
                                                <Box style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    {selectedHospital.specialty.split(',').map((spec, index) => (
                                                        <span key={index} style={{
                                                            backgroundColor: '#e3f2fd',
                                                            padding: '4px 12px',
                                                            borderRadius: '16px',
                                                            fontSize: '0.875rem',
                                                            color: '#1976d2',
                                                            fontWeight: '500'
                                                        }}>
                                                            {spec.trim()}
                                                        </span>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {selectedHospital.latitude && selectedHospital.longitude ? (
                                    <Box style={{
                                        height: '100%',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        border: '1px solid #e3f2fd',
                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.1)'
                                    }}>
                                        <MapContainer 
                                            center={[selectedHospital.latitude, selectedHospital.longitude]} 
                                            zoom={15} 
                                            style={{ height: "400px", width: "100%" }}
                                        >
                                            <TileLayer
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            />
                                            <Marker position={[selectedHospital.latitude, selectedHospital.longitude]}>
                                                <Popup>
                                                    <Box style={{ padding: '8px' }}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: '600', color: '#1976d2', marginBottom: '4px' }}>
                                                            {selectedHospital.name}
                                                        </Typography>
                                                        <Typography variant="body2" style={{ color: '#666' }}>
                                                            {selectedHospital.address}
                                                        </Typography>
                                                    </Box>
                                                </Popup>
                                            </Marker>
                                        </MapContainer>
                                    </Box>
                                ) : (
                                    <Box style={{
                                        padding: '24px',
                                        backgroundColor: '#fff3e0',
                                        borderRadius: '12px',
                                        border: '1px solid #ffe0b2',
                                        textAlign: 'center'
                                    }}>
                                        <Typography variant="body1" color="warning" style={{ fontWeight: '500' }}>
                                            Geocoordinates are not available for this hospital. Please contact an administrator to update the data.
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{
                        padding: '16px 24px',
                        backgroundColor: '#f5f9ff',
                        borderTop: '2px solid #1976d2'
                    }}>
                        <Button 
                            onClick={handleClose} 
                            variant="contained" 
                            color="primary"
                            style={{
                                borderRadius: '12px',
                                padding: '8px 24px',
                                textTransform: 'none',
                                fontWeight: '600',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)'
                                }
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default HospitalTable;
